import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Button,
  Link,
  Switch,
  Dropdown,
  Option,
  TabList,
  Tab,
  Tree,
  TreeItem,
  TreeItemLayout,
  Badge,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowUndo20Regular,
  Save20Regular,
  Code20Regular,
  DocumentText20Regular,
  Add20Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  Eye20Regular,
  ArrowClockwise20Regular,
} from '@fluentui/react-icons';
import {
  getConfigTree,
  getConfigDocument,
  saveConfigDocument,
  getOverrideEnvironments,
  ConfigConcurrencyError,
  type ConfigTree,
  type ConfigKeyNode,
} from '../../api/configStoreClient';
import { useAuthArgs } from '../../hooks/useAuthArgs';
import { RuntimeConfigContext } from '../../config/runtimeConfigContext';
import { JsonDiffView } from './JsonDiffView';
import { MergedPreviewView } from './MergedPreviewView';
import { FeatureFlagToggles } from './FeatureFlagToggles';
import { RunningBuildBanner } from './RunningBuildBanner';
import { RoutingModelFetchPanel } from './RoutingModelFetchPanel';
import { RoutingTpmThresholdControl, type RoutingTpmThresholdProps } from './RoutingTpmThresholdControl';
import { deepMerge, renderMergedWithHighlights } from './configMerge';
import CodeMirror, { type ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { json as jsonLang, jsonParseLinter } from '@codemirror/lang-json';
import { lintGutter, linter } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';
import { syntaxTree, foldEffect, unfoldAll } from '@codemirror/language';

const useStyles = makeStyles({
  // Bound the whole page to the viewport from its (stable) top — brand header + content padding — so the
  // inner editor/table is the ONLY thing that scrolls. Anchoring the height here (not on the grid below)
  // keeps it correct even when the "temporary quick fix" banner wraps to more lines.
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 134px)',
    minHeight: '420px',
    minWidth: 0,
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '300px minmax(0, 1fr)',
    gridTemplateRows: 'minmax(0, 1fr)',
    gap: tokens.spacingHorizontalL,
    alignItems: 'stretch',
    // Fill the remaining space in the page flex column (below the title + banner) and never overflow it,
    // so only the editor/table scrolls — no second, page-level scrollbar.
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    '@media (max-width: 1100px)': {
      gridTemplateColumns: '220px minmax(0, 1fr)',
    },
  },
  tree: {
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingRight: tokens.spacingHorizontalM,
    overflow: 'auto',
    minHeight: 0,
    minWidth: 0,
  },
  editorPane: { display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    marginBottom: tokens.spacingVerticalS,
  },
  layerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
    marginBottom: tokens.spacingVerticalS,
  },
  spacer: { flex: 1 },
  editorFrame: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    // Grow to consume the editor pane's remaining height; the CodeMirror instance fills it 100%.
    flex: 1,
    minHeight: '120px',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    '& .cm-editor': { height: '100%', minHeight: 0 },
    '& .cm-scroller': { overflow: 'auto', minHeight: 0 },
  },
  codeMirror: {
    // The React wrapper is a flex child too; its automatic minimum otherwise keeps the editor content-sized.
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
  },
  crumbs: { color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalXS },
  diffFrame: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    '& .cm-mergeView, & .cm-editor': { height: '100%' },
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: tokens.colorNeutralForeground3,
  },
});

interface Selection {
  group: string;
  collection: string;
  key: string;
  node: ConfigKeyNode;
}

// In-memory model for one layer (Base or an override), preloaded when a key is opened so tab switches
// are instant. `stored` is the last-saved text (server truth); `value` is the current editor text.
interface LayerModel {
  env: string | null; // null = Base
  etag?: string;      // for optimistic-concurrency on save
  exists: boolean;    // false for a new, not-yet-saved override
  stored: string;
  value: string;
  editing: boolean;   // per-tab edit/view mode, so each tab keeps its own toolbar state
}

const NEW_LAYER_STARTER = '{\n}\n';

// Shared CodeMirror extensions for the JSON document editor: JSON language (highlighting, folding,
// bracket matching), an inline JSON linter with a lint gutter, and soft line wrapping so long values
// stay visible without horizontal scrolling.
const JSON_EXTENSIONS = [jsonLang(), linter(jsonParseLinter()), lintGutter(), EditorView.lineWrapping];

// "Collapse all" that KEEPS THE ROOT EXPANDED and folds only its direct children — so a routing
// document shows `{ "rules": {…}, "default": {…}, "defaultHumanAgent": "…" }` rather than a single
// `{…}`. Walks the JSON syntax tree and folds every Object/Array container at nesting depth 2 (the
// root container is depth 1). Returns true when it folded at least one node.
function foldToTopLevel(view: EditorView): boolean {
  const tree = syntaxTree(view.state);
  const effects: ReturnType<typeof foldEffect.of>[] = [];
  let depth = 0;
  tree.iterate({
    enter: (node) => {
      if (node.name === 'Object' || node.name === 'Array') {
        depth += 1;
        if (depth === 2) {
          const from = node.from + 1;
          const to = node.to - 1;
          if (to > from) {
            effects.push(foldEffect.of({ from, to }));
          }
        }
      }
    },
    leave: (node) => {
      if (node.name === 'Object' || node.name === 'Array') {
        depth -= 1;
      }
    },
  });
  if (effects.length === 0) {
    return false;
  }
  view.dispatch({ effects });
  return true;
}

/**
 * Admin > Configuration Store. Browse the Group → Collection → Key tree on the left and view/edit the
 * selected JSON document on the right. Documents open read-only (view mode); toggling Edit enables editing
 * and reveals Undo (revert to the last-loaded value) and Save (guarded by a confirmation dialog).
 *
 * Configuration promotion: each key has a Base layer plus optional per-environment override layers (ppe,
 * prod, …) that layer over the base exactly like appsettings.json + appsettings.&lt;env&gt;.json. The layer
 * selector switches between Base and each environment override, and "Add override" creates a new one. Saves
 * use the layer's ETag for optimistic concurrency.
 */
export function ConfigStorePage({ canViewRoutingDecisions = false }: { canViewRoutingDecisions?: boolean }) {
  const styles = useStyles();
  const getAuthArgs = useAuthArgs();

  // The environment this API instance runs in (author/int/ppe/prod/local), from the SPA runtime
  // config. Shipped config-store documents are overwritten on deployment; generated routing seeds
  // are persisted separately and are never edited through this JSON editor.
  const runtimeCfg = useContext(RuntimeConfigContext);
  const runningEnv = (runtimeCfg?.environment ?? 'local').trim();
  const isNonAuthorEnv = !['author', 'local', ''].includes(runningEnv.toLowerCase());

  // Per-environment write restriction (mirrors the server): a deployed stamp may edit only the Base layer
  // and its OWN environment override — Author edits Base + author, PPE edits Base + ppe, etc. Local dev
  // ('local' / empty) is an unshared sandbox and stays unrestricted. Base is always editable.
  const isLocalEnv = ['local', ''].includes(runningEnv.toLowerCase());
  const canEditLayer = useCallback(
    (env: string | null) => env === null || isLocalEnv || env.toLowerCase() === runningEnv.toLowerCase(),
    [isLocalEnv, runningEnv],
  );

  const [tree, setTree] = useState<ConfigTree | null>(null);
  const [selection, setSelection] = useState<Selection | null>(null);
  // Per-layer models, preloaded when a key is opened so switching tabs is instant (no fetch/spinner) and
  // each tab keeps its own content. Keyed by layer ('__base__' for Base, else the env token).
  const [models, setModels] = useState<Record<string, LayerModel>>({});
  const [activeKey, setActiveKey] = useState<string>('__base__');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fetchingModels, setFetchingModels] = useState(false);
  const [thresholdInputError, setThresholdInputError] = useState<string | null>(null);
  const fetchInFlight = useRef(false);
  const mounted = useRef(false);
  const keyRequest = useRef(0);
  const treeRequest = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      keyRequest.current += 1;
      treeRequest.current += 1;
    };
  }, []);

  const startModelFetch = useCallback(() => {
    // Synchronous lock also covers rapid double-clicks and leaving/reopening the environment tab.
    if (fetchInFlight.current || !mounted.current) return false;
    fetchInFlight.current = true;
    setFetchingModels(true);
    return true;
  }, []);
  const endModelFetch = useCallback(() => {
    fetchInFlight.current = false;
    if (mounted.current) setFetchingModels(false);
  }, []);

  const [addingLayer, setAddingLayer] = useState(false);
  const [newLayerName, setNewLayerName] = useState('');
  const [overrideEnvs, setOverrideEnvs] = useState<string[]>([]);

  const [loadingTree, setLoadingTree] = useState(false);
  const [loadingKey, setLoadingKey] = useState(false); // initial load of a key's layers (not per-tab)
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // CodeMirror instance handle, used to drive Collapse all / Expand all fold commands.
  const cmRef = useRef<ReactCodeMirrorRef>(null);
  const runFold = useCallback((cmd: (view: EditorView) => boolean) => {
    const view = cmRef.current?.view;
    if (view) {
      cmd(view);
      view.focus();
    }
  }, []);

  const layerKey = (env: string | null) => env ?? '__base__';

  // Derived view of the active layer.
  const environment = activeKey === '__base__' ? null : activeKey;
  const active = models[activeKey];
  const value = active?.value ?? '';
  const exists = active?.exists ?? false;
  const stored = active?.stored ?? '';
  const isGeneratedSeed = selection?.group === 'routing' && selection.collection === 'model-deployments';
  const isModelResources = selection?.group === 'routing'
    && selection.collection === 'model-endpoints' && selection.key === 'default';
  const isFetchEnvironment = environment !== null && !!runtimeCfg?.environment.trim()
    && environment.toLowerCase() === runningEnv.toLowerCase();
  const editing = !isGeneratedSeed && (active?.editing ?? false); // per-tab edit/view mode
  const activeLayerEditable = !isGeneratedSeed && canEditLayer(environment);
  const isLayerDirty = (m: LayerModel | undefined) =>
    !m ? false : m.exists ? m.value !== m.stored : m.value.trim().length > 0;
  const dirty = isLayerDirty(active);

  // Update the active layer's in-memory value (per-tab; nothing hits the server until Save).
  const setActiveValue = useCallback((next: string) => {
    setModels((m) => (m[activeKey] ? { ...m, [activeKey]: { ...m[activeKey], value: next } } : m));
  }, [activeKey]);

  const thresholdProps: RoutingTpmThresholdProps = {
    environment,
    baseSavedJson: models['__base__']?.exists ? models['__base__'].stored : '{}',
    baseDraftJson: models['__base__']?.value ?? '{}',
    savedJson: active?.exists ? stored : '{}',
    draftJson: value,
    editable: activeLayerEditable,
    editing,
    busy: loadingTree || loadingKey || saving || reloading || fetchingModels,
    onChange: setActiveValue,
    onValidationChange: setThresholdInputError,
  };

  // Toggle the active layer's edit/view mode (per-tab).
  const setActiveEditing = useCallback((next: boolean) => {
    setModels((m) => (m[activeKey] ? { ...m, [activeKey]: { ...m[activeKey], editing: next } } : m));
  }, [activeKey]);

  const jsonError = useMemo(() => {
    if (!editing) return null;
    try {
      JSON.parse(value);
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : 'Invalid JSON';
    }
  }, [editing, value]);

  const loadTree = useCallback(async () => {
    const request = ++treeRequest.current;
    const current = () => mounted.current && treeRequest.current === request;
    setLoadingTree(true);
    setError(null);
    try {
      const auth = await getAuthArgs();
      if (!current()) return;
      if (!auth) {
        setError('You must be signed in to manage configuration.');
        return;
      }
      const next = await getConfigTree(auth);
      if (current()) setTree(next);
    } catch (err) {
      if (current()) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (current()) setLoadingTree(false);
    }
  }, [getAuthArgs]);

  useEffect(() => { void loadTree(); }, [loadTree]);

  // Load the selectable override environments (from the dbo.KeyValueStore setting) once on mount.
  useEffect(() => {
    void (async () => {
      try {
        const auth = await getAuthArgs();
        if (!auth) return;
        setOverrideEnvs(await getOverrideEnvironments(auth));
      } catch {
        // Non-fatal: the picker falls back to a free-text input when the list is unavailable.
      }
    })();
  }, [getAuthArgs]);

  // Open a key: load Base + every existing override in parallel and build the per-layer model map. This
  // is the ONLY network fetch — subsequent tab switches read from these models, so they are instant.
  const openKey = useCallback(async (sel: Selection) => {
    const request = ++keyRequest.current;
    const current = () => mounted.current && keyRequest.current === request;
    setSelection(sel);
    setActiveKey('__base__');
    setAddingLayer(false);
    setError(null);
    setNotice(null);
    setConfirmOpen(false);
    setPreviewOpen(false);
    setModels({});
    // Feature flags render as a dedicated toggle table (FeatureFlagToggles) that loads its own layers, so
    // skip the JSON per-layer model load for that group.
    if (sel.group === 'feature-flags') {
      setLoadingKey(false);
      return;
    }
    setLoadingKey(true);
    try {
      const auth = await getAuthArgs();
      if (!current()) return;
      if (!auth) {
        setError('You must be signed in to manage configuration.');
        return;
      }
      const envs = sel.node.environments;
      const layerEnvs: (string | null)[] = [null, ...envs];
      const docs = await Promise.all(
        layerEnvs.map((env) => getConfigDocument(auth, sel.group, sel.collection, sel.key, env)),
      );
      if (!current()) return;
      const built: Record<string, LayerModel> = {};
      layerEnvs.forEach((env, i) => {
        const loaded = docs[i];
        const storedText = loaded ? loaded.json : (env === null ? '{\n}\n' : NEW_LAYER_STARTER);
        built[layerKey(env)] = {
          env,
          etag: loaded?.etag ?? undefined,
          exists: loaded !== null,
          stored: storedText,
          value: storedText,
          editing: false, // existing layers open read-only
        };
      });
      setModels(built);
    } catch (err) {
      if (current()) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (current()) setLoadingKey(false);
    }
  }, [getAuthArgs]);

  const onToggleEdit = useCallback((next: boolean) => {
    if (next && !activeLayerEditable) {
      return; // other-env overrides are read-only in this environment (enforced server-side too)
    }
    if (!next && dirty && exists) {
      setActiveValue(stored); // discard unsaved edits back to the stored value
    }
    setActiveEditing(next);
    setNotice(null);
  }, [activeLayerEditable, dirty, exists, stored, setActiveValue, setActiveEditing]);

  const onUndo = useCallback(() => {
    setActiveValue(exists ? stored : (environment === null ? '{\n}\n' : NEW_LAYER_STARTER));
  }, [exists, stored, environment, setActiveValue]);

  const onFormat = useCallback(() => {
    try {
      setActiveValue(JSON.stringify(JSON.parse(value), null, 2));
    } catch {
      // ignore — invalid JSON is reported inline by jsonError.
    }
  }, [value, setActiveValue]);

  const doSave = useCallback(async () => {
    if (!selection || !active || !activeLayerEditable || fetchInFlight.current || thresholdInputError) return;
    // Never persist invalid JSON, even if the Save gate was somehow bypassed.
    try {
      JSON.parse(value);
    } catch (e) {
      setError(`Cannot save invalid JSON: ${e instanceof Error ? e.message : 'parse error'}`);
      setConfirmOpen(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to save.');
        return;
      }
      const saved = await saveConfigDocument(auth, {
        group: selection.group,
        collection: selection.collection,
        key: selection.key,
        environment,
        json: value,
        etag: active.etag,
      });
      // Update this layer's model so it is now clean (clears the tab "*") and returns to view mode.
      setModels((m) => ({
        ...m,
        [activeKey]: { ...m[activeKey], exists: true, stored: saved.json, value: saved.json, etag: saved.etag ?? undefined, editing: false },
      }));
      setNotice('Saved.');
      void loadTree(); // a newly created override should appear in the tree
    } catch (err) {
      if (err instanceof ConfigConcurrencyError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  }, [selection, active, activeLayerEditable, environment, value, activeKey, getAuthArgs, loadTree, thresholdInputError]);

  // Re-fetch the active layer from the store, refreshing its stored baseline and ETag while KEEPING the
  // user's in-progress edits, so after a "changed by someone else" conflict they can review against the new
  // baseline (diff/preview) and Save to reapply their edits against the fresh ETag.
  const onReload = useCallback(async () => {
    if (!selection || !active) return;
    setReloading(true);
    setError(null);
    setNotice(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to reload.');
        return;
      }
      const loaded = await getConfigDocument(auth, selection.group, selection.collection, selection.key, environment);
      const storedText = loaded ? loaded.json : (environment === null ? '{\n}\n' : NEW_LAYER_STARTER);
      setModels((m) => {
        const cur = m[activeKey];
        if (!cur) return m;
        return {
          ...m,
          // Refresh the stored baseline + ETag; preserve the edited value and edit mode so edits aren't lost.
          [activeKey]: { ...cur, etag: loaded?.etag ?? undefined, exists: loaded !== null, stored: storedText },
        };
      });
      void loadTree(); // the layer may have appeared/disappeared since it was opened
      setNotice(dirty ? 'Reloaded the latest version. Review the diff and Save to reapply your edits.' : 'Reloaded the latest version.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setReloading(false);
    }
  }, [selection, active, environment, activeKey, dirty, getAuthArgs, loadTree]);

  const confirmAddLayer = useCallback(() => {
    const choice = newLayerName.trim();
    if (!selection || !choice || isGeneratedSeed) return;
    setNewLayerName('');
    setAddingLayer(false);
    // "Default" maps to the Base layer (no environment); everything else is a lowercase override token.
    if (choice.toLowerCase() === 'default') {
      setActiveKey('__base__');
      return;
    }
    const env = choice.toLowerCase();
    if (!/^[A-Za-z0-9_-]+$/.test(env)) {
      setError('Environment name may only contain letters, digits, hyphen and underscore.');
      return;
    }
    // Existing model → just activate it; otherwise create a new in-memory (unsaved) layer model in edit mode.
    if (!models[env]) {
      setModels((m) => ({
        ...m,
        [env]: { env, exists: false, stored: NEW_LAYER_STARTER, value: NEW_LAYER_STARTER, editing: true },
      }));
    }
    setActiveKey(env);
  }, [newLayerName, selection, models, isGeneratedSeed]);

  // Options for the "Add override" picker: the configured environments minus any layer that already
  // has a model (saved or pending). Base (Default) always exists, so it is never offered. In a deployed
  // stamp the only override you may create is your OWN environment (Author → author, PPE → ppe, …); local
  // dev may create any.
  const overrideOptions = useMemo(() => {
    if (isGeneratedSeed) return [];
    const existing = new Set(Object.keys(models).map((k) => k.toLowerCase()));
    return overrideEnvs.filter(
      (e) =>
        e.toLowerCase() !== 'default' &&
        !existing.has(e.toLowerCase()) &&
        canEditLayer(e.toLowerCase()),
    );
  }, [overrideEnvs, models, canEditLayer, isGeneratedSeed]);

  // Tabs come straight from the loaded model map, in insertion order (Base first, then overrides).
  const layers: (string | null)[] = useMemo(
    () => Object.values(models).map((m) => m.env),
    [models],
  );

  // Merged Base + active override preview: parses both, deep-merges (objects by key; scalars/arrays
  // override), and marks the override's contributed lines for highlighting. Only meaningful on an
  // override tab. Returns a parse error message instead of a merge when either side is invalid JSON.
  const preview = useMemo(() => {
    if (environment === null) return null;
    const baseText = models['__base__']?.value ?? '{}';
    try {
      const baseObj = JSON.parse(baseText);
      const overrideObj = JSON.parse(value);
      const merged = deepMerge(baseObj, overrideObj);
      return { ...renderMergedWithHighlights(baseObj, merged), error: null as string | null };
    } catch (e) {
      return { text: '', highlightLines: [], error: e instanceof Error ? e.message : 'Invalid JSON' };
    }
  }, [environment, models, value]);

  return (
    <section className={styles.page}>
      <Text as="h1" size={600} weight="semibold" style={{ display: 'block', marginBottom: 12 }}>
        Configuration Store
      </Text>

      <MessageBar intent="info" style={{ marginBottom: 12 }}>
        <MessageBarBody>
          {isGeneratedSeed ? <>
            This is a generated, server-persisted routing seed. It is read-only and is not overwritten by deployment.
            Use <strong>Fetch models</strong> on <strong>routing / model-endpoints / default</strong> in the running
            environment's tab to replace it.
          </> : <>
          Edits here are a <strong>temporary quick fix</strong>. The configuration files shipped with the app
          are the source of truth: every deployment <strong>overwrites</strong> these documents from those
          files, so a change made here is reset on the next deploy{runningEnv ? <> of <strong>{runningEnv}</strong></> : null}.
          For a permanent change, update the config-store document files (<code>config-store/…</code>) in source control and deploy.
          {' '}Generated model-deployment seeds are separate and persist across deployments.
          </>}
        </MessageBarBody>
      </MessageBar>

      <RunningBuildBanner onAccepted={() => { void loadTree(); }} />

      {error && <MessageBar intent="error" style={{ marginBottom: 12 }}><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {notice && <MessageBar intent="success" style={{ marginBottom: 12 }}><MessageBarBody>{notice}</MessageBarBody></MessageBar>}

      <div className={styles.layout}>
        <div className={styles.tree}>
          {loadingTree ? (
            <Spinner size="tiny" label="Loading…" />
          ) : !tree || tree.groups.length === 0 ? (
            <Text size={200} italic>No configuration documents found.</Text>
          ) : (
            <Tree aria-label="Configuration tree" defaultOpenItems={tree.groups.map((g) => `g:${g.name}`)}>
              {tree.groups.map((g) => (
                <TreeItem key={g.name} itemType="branch" value={`g:${g.name}`}>
                  <TreeItemLayout>{g.name}</TreeItemLayout>
                  <Tree>
                    {g.collections.map((c) => (
                      <TreeItem key={c.name} itemType="branch" value={`c:${g.name}/${c.name}`}>
                        <TreeItemLayout>{c.name}</TreeItemLayout>
                        <Tree>
                          {c.keys.map((k) => {
                            const isActive =
                              selection?.group === g.name &&
                              selection?.collection === c.name &&
                              selection?.key === k.name;
                            return (
                              <TreeItem key={k.name} itemType="leaf" value={`k:${g.name}/${c.name}/${k.name}`}>
                                <TreeItemLayout
                                  onClick={() => void openKey({ group: g.name, collection: c.name, key: k.name, node: k })}
                                  style={{
                                    cursor: 'pointer',
                                    fontWeight: isActive ? tokens.fontWeightSemibold : undefined,
                                    color: isActive ? tokens.colorBrandForeground1 : undefined,
                                  }}
                                >
                                  {k.name}
                                  {k.environments.length > 0 && (
                                    <Badge appearance="tint" size="small" style={{ marginLeft: 6 }}>
                                      {k.environments.length + 1} layers
                                    </Badge>
                                  )}
                                </TreeItemLayout>
                              </TreeItem>
                            );
                          })}
                        </Tree>
                      </TreeItem>
                    ))}
                  </Tree>
                </TreeItem>
              ))}
            </Tree>
          )}
        </div>

        <div className={styles.editorPane}>
          {!selection ? (
            <div className={styles.placeholder}>
              <Text italic>Select a document from the tree to view it.</Text>
            </div>
          ) : selection.group === 'feature-flags' ? (
            <>
              <div className={styles.crumbs}>
                <Text size={200}>
                  {selection.group} / {selection.collection} / <strong>{selection.key}</strong>
                </Text>
              </div>
              <FeatureFlagToggles
                getAuthArgs={getAuthArgs}
                group={selection.group}
                collection={selection.collection}
                keyName={selection.key}
                overrideEnvs={overrideEnvs}
                runningEnv={runningEnv}
                isLocalEnv={isLocalEnv}
                onTreeChanged={loadTree}
              />
            </>
          ) : (
            <>
              <div className={styles.crumbs}>
                <Text size={200}>
                  {selection.group} / {selection.collection} / <strong>{selection.key}</strong>
                </Text>
              </div>

              <div className={styles.layerRow}>
                {isModelResources && canViewRoutingDecisions && (
                  <Link href="#/admin/routing-decisions">Why this route — sampled decisions</Link>
                )}
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Layer:</Text>
                <TabList
                  size="small"
                  selectedValue={activeKey}
                  onTabSelect={(_, d) => {
                    // Instant, in-memory switch — no fetch, no spinner. Edit/view mode, content, and dirty
                    // state all come from the target tab's own model, so switching is fully per-tab.
                    setActiveKey(d.value as string);
                    setNotice(null);
                  }}
                >
                  {layers.map((env) => {
                    const k = layerKey(env);
                    return (
                      <Tab key={k} value={k}>
                        {(env ?? 'Base') + (isLayerDirty(models[k]) ? ' *' : '')}
                      </Tab>
                    );
                  })}
                </TabList>
                {!isGeneratedSeed && (addingLayer ? (
                  <>
                    <Dropdown
                      size="small"
                      placeholder="Select environment"
                      selectedOptions={newLayerName ? [newLayerName] : []}
                      value={newLayerName}
                      onOptionSelect={(_, d) => setNewLayerName(d.optionValue ?? '')}
                      style={{ minWidth: 150 }}
                    >
                      {overrideOptions.map((env) => (
                        <Option key={env} value={env}>{env}</Option>
                      ))}
                    </Dropdown>
                    <Button size="small" appearance="primary" onClick={confirmAddLayer} disabled={!newLayerName.trim()}>
                      Add
                    </Button>
                    <Button size="small" appearance="subtle" onClick={() => { setAddingLayer(false); setNewLayerName(''); }}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  overrideOptions.length > 0 && (
                    <Button size="small" appearance="subtle" icon={<Add20Regular />} onClick={() => setAddingLayer(true)}>
                      Add override
                    </Button>
                  )
                ))}
              </div>

              {loadingKey ? (
                <Spinner label="Loading layers…" />
              ) : (
                <>
                  {isModelResources && isFetchEnvironment && environment !== null && (
                    <RoutingModelFetchPanel
                      key={environment}
                      environment={environment}
                      baseJson={models['__base__']?.exists ? models['__base__'].stored : '{}'}
                      environmentJson={active?.exists ? active.stored : '{}'}
                      baseETag={models['__base__']?.etag ?? null}
                      environmentETag={active?.etag ?? null}
                      environmentExists={exists}
                      dirty={models['__base__']?.value !== models['__base__']?.stored || dirty}
                      busy={loadingTree || loadingKey || saving || reloading}
                      fetching={fetchingModels}
                      getAuthArgs={getAuthArgs}
                      onFetchStart={startModelFetch}
                      onFetchEnd={endModelFetch}
                      onSeedSaved={loadTree}
                      threshold={thresholdProps}
                    />
                  )}
                  {isModelResources && !isFetchEnvironment && (
                    <RoutingTpmThresholdControl key={activeKey} {...thresholdProps} />
                  )}
                  {isModelResources && environment === null && (
                    <MessageBar intent="info" style={{ marginBottom: 8 }}>
                      <MessageBarBody>
                        Fetch models is not available from Base. Select the <strong>{runningEnv}</strong> environment
                        tab (add and save that override if needed) to query Azure and update its saved routing seed.
                        Other environment tabs cannot fetch from this API.
                      </MessageBarBody>
                    </MessageBar>
                  )}
                  <div className={styles.toolbar}>
                    {!isGeneratedSeed && <Switch
                      checked={editing}
                      disabled={!activeLayerEditable}
                      onChange={(_, d) => onToggleEdit(d.checked)}
                      label={editing ? 'Editing' : 'View only'}
                    />}
                    {!activeLayerEditable && !isGeneratedSeed && (
                      <Badge appearance="tint" color="danger">
                        Read-only in {runningEnv}
                      </Badge>
                    )}
                    {!editing ? (
                      <Badge appearance="tint" icon={<DocumentText20Regular />}>Read-only</Badge>
                    ) : (
                      <Badge appearance="tint" color="warning">Edit mode</Badge>
                    )}
                    {environment === null ? (
                      <Badge appearance="tint" color="informative">Base</Badge>
                    ) : (
                      <Badge appearance="tint" color="brand">{environment} override</Badge>
                    )}
                    {!exists && !isGeneratedSeed && <Badge appearance="tint" color="severe">New layer</Badge>}
                    {dirty && !isGeneratedSeed && <Badge appearance="tint" color="brand">Unsaved changes</Badge>}

                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<ChevronRight20Regular />}
                      onClick={() => runFold(foldToTopLevel)}
                      title="Collapse all sections (keep the root open)"
                    >
                      Collapse all
                    </Button>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<ChevronDown20Regular />}
                      onClick={() => runFold(unfoldAll)}
                      title="Expand all sections"
                    >
                      Expand all
                    </Button>
                    {environment !== null && !isGeneratedSeed && (
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<Eye20Regular />}
                        onClick={() => setPreviewOpen(true)}
                        title="Preview the merged Base + this override result"
                      >
                        Preview
                      </Button>
                    )}

                    <div className={styles.spacer} />

                    {editing && (
                      <>
                        <Button
                          appearance="subtle"
                          icon={<ArrowClockwise20Regular />}
                          onClick={() => void onReload()}
                          disabled={reloading || saving || fetchingModels}
                          title="Reload the latest version from the store (refreshes the version tag; keeps your edits so you can Save to reapply)"
                        >
                          Reload
                        </Button>
                        <Button appearance="subtle" icon={<Code20Regular />} onClick={onFormat}>Format</Button>
                        <Button appearance="subtle" icon={<ArrowUndo20Regular />} onClick={onUndo} disabled={!dirty}>
                          Undo
                        </Button>
                        <Button
                          appearance="primary"
                          icon={<Save20Regular />}
                          onClick={() => setConfirmOpen(true)}
                          disabled={!dirty || !!jsonError || !!thresholdInputError || saving || fetchingModels}
                        >
                          Save
                        </Button>
                      </>
                    )}
                  </div>

                  {editing && jsonError && (
                    <MessageBar intent="warning" style={{ marginBottom: 8 }}>
                      <MessageBarBody>Invalid JSON: {jsonError}</MessageBarBody>
                    </MessageBar>
                  )}

                  {environment !== null && !isGeneratedSeed && (
                    <MessageBar intent="info" style={{ marginBottom: 8 }}>
                      <MessageBarBody>
                        This is the <strong>{environment}</strong> override. Include only this environment's
                        delta — it layers on top of Base (keyed entries merge by key; scalars override).
                      </MessageBarBody>
                    </MessageBar>
                  )}

                  <div className={styles.editorFrame}>
                    <CodeMirror
                      ref={cmRef}
                      className={styles.codeMirror}
                      value={value}
                      height="100%"
                      editable={editing}
                      readOnly={!editing}
                      extensions={JSON_EXTENSIONS}
                      onChange={(next) => setActiveValue(next)}
                      basicSetup={{
                        lineNumbers: true,
                        foldGutter: true,
                        bracketMatching: true,
                        closeBrackets: true,
                        highlightActiveLine: editing,
                        highlightActiveLineGutter: editing,
                      }}
                      aria-label="Document JSON"
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog open={confirmOpen && !isGeneratedSeed} onOpenChange={(_, d) => setConfirmOpen(d.open)}>
        <DialogSurface style={{ maxWidth: '95vw', width: '1100px' }}>
          <DialogBody>
            <DialogTitle>{exists ? 'Review changes before saving' : 'Review new layer before creating'}</DialogTitle>
            <DialogContent>
              {isNonAuthorEnv && (
                <MessageBar intent="warning" style={{ marginBottom: 12 }}>
                  <MessageBarBody>
                    ⚠️ This is a temporary quick fix. The next deployment of <strong>{runningEnv}</strong> will
                    overwrite it from the shipped configuration files. For a permanent change, update the
                    config-store document files (<code>config-store/…</code>) in source control and deploy.
                  </MessageBarBody>
                </MessageBar>
              )}
              {selection && (
                <Text style={{ display: 'block', marginBottom: 8 }}>
                  This will {exists ? 'overwrite' : 'create'}{' '}
                  <strong>
                    {selection.group} / {selection.collection} / {selection.key}
                    {environment ? ` [${environment}]` : ' [Base]'}
                  </strong>
                  . Review the highlighted changes below, then confirm. The change may take effect on running
                  services within moments.
                </Text>
              )}
              <div style={{ display: 'flex', gap: 0, marginBottom: 4 }}>
                <Text size={200} weight="semibold" style={{ flex: 1, color: tokens.colorNeutralForeground3 }}>
                  Before ({exists ? 'stored' : 'empty'})
                </Text>
                <Text size={200} weight="semibold" style={{ flex: 1, color: tokens.colorNeutralForeground3 }}>
                  After (your edits)
                </Text>
              </div>
              <div className={styles.diffFrame}>
                <JsonDiffView before={exists ? stored : ''} after={value} height="55vh" />
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button appearance="primary" icon={<Save20Regular />} onClick={() => void doSave()} disabled={saving || fetchingModels || !!thresholdInputError}>
                {saving ? 'Saving…' : exists ? 'Save' : 'Create'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={(_, d) => setPreviewOpen(d.open)}>
        <DialogSurface style={{ maxWidth: '95vw', width: '1100px' }}>
          <DialogBody>
            <DialogTitle>Preview — Base + {environment} (merged result)</DialogTitle>
            <DialogContent>
              <Text style={{ display: 'block', marginBottom: 8 }}>
                Final configuration the runtime uses for <strong>{environment}</strong>: the <strong>{environment}</strong>{' '}
                override layered on top of Base (objects merge by key; scalars and arrays override). Highlighted lines
                are the override's contribution.
              </Text>
              {preview?.error ? (
                <MessageBar intent="warning">
                  <MessageBarBody>Cannot preview — invalid JSON: {preview.error}</MessageBarBody>
                </MessageBar>
              ) : (
                <div className={styles.diffFrame}>
                  <MergedPreviewView
                    text={preview?.text ?? ''}
                    highlightLines={preview?.highlightLines ?? []}
                    height="60vh"
                  />
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setPreviewOpen(false)}>Close</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </section>
  );
}
